import { useMemo } from 'react';
import { Box, Text, Toast, useToast } from '@zillow/constellation-10';
import { addMonths, startOfMonth, subQuarters, subYears } from 'date-fns';
import { AdSpendTable } from './AdSpendReportTable';
import { AdvertisingSpend } from './Graphs/AdvertisingSpend';
import { generateAdvertisingSpendData } from './Graphs/AdvertisingSpend.utils';
import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';

import { CustomerAnalyticsPage } from '../../../SharedComponents/CustomerAnalyticsPage';
import { AdSpendSidebar } from './AdSpendReportSidebar';
import type { FileType, SelectedGraphs } from '../../../SharedComponents/ExportDialog';
import { extractAdSpendData, getAdSpendGraphConfigs } from './AdSpendReport.export';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { downloadExcel } from '../../../../utils/exportUtils';
import { downloadPDF } from '../../../../utils/pdfExportUtils';
import { captureGraphs } from '../../../../utils/captureGraphs';
import { buildCoverPageData } from '../../../../utils/buildCoverPageData';
import { emitAdSpendExportEvent, emitAdSpendExportPdfEvent } from '../../../../analytics/events';
import { usePortalAnalyticsWebApiHandler_getSfhAccountLevelSpend } from '@zg-rentals/ts-customer-analytics-client-react-query';

export const AdSpendReport = () => {
  const { selectedRegionId, filteredProperties, selectedFilters, regionsMapping } = useMainLayout();
  const { user } = useRentalManagerUser();
  const { enqueueToast } = useToast();

  const today = new Date();
  const oneYearAgo = startOfMonth(addMonths(subYears(today, 1), 1));
  const oneYearAndOneQuarterAgo = startOfMonth(subQuarters(oneYearAgo, 1));

  const { data: sfhAccountLevelSpendData } = usePortalAnalyticsWebApiHandler_getSfhAccountLevelSpend({
    startDate: {
      year: oneYearAndOneQuarterAgo.getFullYear(),
      month: oneYearAndOneQuarterAgo.getMonth() + 1,
      day: oneYearAndOneQuarterAgo.getDate(),
    },
    endDate: {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    },
  });

  const noFiltersSelected =
    selectedFilters.package === '' &&
    selectedFilters.stateRegionId === 0 &&
    selectedFilters.metropolitanServiceAreaRegionId === 0 &&
    selectedFilters.cityRegionId === 0 &&
    selectedFilters.zipCodeRegionId === 0 &&
    selectedFilters.listingAlias === '';

  const hasSelectedProperty = selectedFilters.listingAlias !== '';

  const { chartData: advertisingSpendChartData, allPackageTypesPresent } = useMemo(
    () =>
      generateAdvertisingSpendData({
        filteredProperties,
        sfhAccountLevelSpendData,
        noFiltersSelected,
        selectedPackage: selectedFilters.package,
        hasSelectedProperty,
      }),
    [filteredProperties, sfhAccountLevelSpendData, noFiltersSelected, selectedFilters.package, hasSelectedProperty],
  );

  const graphConfigs = getAdSpendGraphConfigs();

  const handleExport = async (fileType: FileType, selectedGraphs: SelectedGraphs) => {
    if (user) {
      const emitMap = { xls: emitAdSpendExportEvent, pdf: emitAdSpendExportPdfEvent } as const;
      emitMap[fileType]?.({ user });
    }

    try {
      switch (fileType) {
        case 'xls': {
          const graphsData = extractAdSpendData(selectedGraphs, advertisingSpendChartData, allPackageTypesPresent);
          if (graphsData.length === 0) {
            enqueueToast(
              <Toast tone="critical">
                <Text>No data to export.</Text>
              </Toast>,
            );
            return;
          }
          await downloadExcel(graphsData, 'Ad_Spend');
          break;
        }
        case 'pdf': {
          const graphImages = await captureGraphs('Ad Spend', selectedGraphs);
          const tableData = extractAdSpendData(selectedGraphs, advertisingSpendChartData, allPackageTypesPresent);

          const pdfGraphPages = graphImages.map((graph) => {
            const normalizedGraphName = graph.name.toLowerCase().replace(/^total\s+/, '');
            const matchingTable = tableData.find((td) => {
              const normalizedTableName = td.name
                .toLowerCase()
                .replace(/_/g, ' ')
                .replace(/\s+(multifamily|single family)$/, '');
              return (
                normalizedTableName === normalizedGraphName ||
                normalizedTableName.startsWith(normalizedGraphName) ||
                normalizedGraphName.startsWith(normalizedTableName)
              );
            });
            return {
              name: graph.name,
              imageData: graph.imageData,
              tableData: matchingTable,
              legend: graph.legend,
              description: graph.description,
            };
          });

          if (pdfGraphPages.length === 0) {
            enqueueToast(
              <Toast tone="critical">
                <Text>No data to export.</Text>
              </Toast>,
            );
            return;
          }

          const coverPageData = await buildCoverPageData(
            'Ad spend',
            selectedFilters,
            regionsMapping,
            filteredProperties,
          );
          await downloadPDF(pdfGraphPages, 'Ad Spend', [], coverPageData);
          break;
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to export data.';
      enqueueToast(
        <Toast tone="critical">
          <Text>{errorMessage}</Text>
        </Toast>,
      );
      throw error;
    }
  };

  return (
    <CustomerAnalyticsPage
      header="Ad spend"
      subheader="Source: Zillow internal data"
      sidebarTitle="Current ad package distribution"
      sidebar={<AdSpendSidebar />}
      tabName="Ad Spend"
      graphConfigs={graphConfigs}
      onExport={handleExport}
      hideMarketSelect
    >
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
        <AdvertisingSpend
          selectedRegionId={selectedRegionId}
          chartData={advertisingSpendChartData}
          allPackageTypesPresent={allPackageTypesPresent}
        />
        <AdSpendTable />
      </Box>
    </CustomerAnalyticsPage>
  );
};
